import React, { useCallback, useEffect, useState } from 'react';
import { NumberInput, StringInput, SeverityInput, StringArrayInput } from './CustomizableFieldsInputs';
import { ConfigDefinition } from './types';
import { SEVERITY } from '@silverfort/sf-components';

interface CustomizableFieldsProps {
    customizableFields: Record<string, ConfigDefinition>;
    setCustomizableFields: (customizableFields: Record<string, ConfigDefinition>) => void;
    setIsFormValid: (isValid: boolean) => void;
}

const CustomizableFields: React.FC<CustomizableFieldsProps> = ({
    customizableFields,
    setCustomizableFields,
    setIsFormValid
}) => {
    const [fieldValidity, setFieldValidity] = useState<Record<string, boolean>>();

    const setField = (key: string, type: 'number' | 'string' | 'severity', value: string | SEVERITY) => {
        let newValue: number | string | null | undefined;
        if (type === 'number' || type === 'severity') {
            newValue = value === '' ? null : Number(value);
        } else {
            newValue = value === '' ? null : value;
        }
        const prevDef = customizableFields[key];
        setCustomizableFields({
            ...customizableFields,
            [key]: {
                ...prevDef,
                currentValue: newValue,
                changed: true
            }
        });
    };

    const setStringArrayField = (key: string, value: string[]) => {
        const prevDef = customizableFields[key];
        setCustomizableFields({
            ...customizableFields,
            [key]: {
                ...prevDef,
                currentValue: value,
                changed: true
            }
        });
    };

    const handleValidityChange = useCallback((key: string, isValid: boolean) => {
        setFieldValidity((prev) => ({ ...(prev ?? {}), [key]: isValid }));
    }, []);

    useEffect(() => {
        const isValid = !fieldValidity || Object.values(fieldValidity).every((f) => f === undefined || f);
        setIsFormValid(isValid);
    }, [fieldValidity, setIsFormValid]);

    return (
        <div className="flex gap-6 h-full w-full min-w-[300px]">
            {customizableFields && (
                <div className="border border-gray-200 rounded-lg p-6 flex flex-col h-full">
                    <h3 className="text-sm font-medium  mb-4">Alert thresholds</h3>
                    <div className="space-y-4 flex-1">
                        {Object.entries(customizableFields).map(([key, field]) => {
                            const currentVal = customizableFields?.[key]?.currentValue;
                            const configVal =
                                currentVal !== undefined ? currentVal : customizableFields?.[key]?.defaultValue;

                            const value = configVal === undefined || configVal === null ? '' : String(configVal);
                            const numericValue = configVal === undefined || configVal === null ? 0 : Number(configVal);

                            return (
                                <div key={key} className="flex items-center">
                                    {(() => {
                                        switch (field.jsonSchema.type) {
                                            case 'number':
                                                return (
                                                    <NumberInput
                                                        displayName={field.displayName}
                                                        value={value}
                                                        schema={{
                                                            minimum: field.jsonSchema.minimum ?? 1,
                                                            maximum: field.jsonSchema.maximum ?? 3650
                                                        }}
                                                        onChange={(val) => setField(key, 'number', val)}
                                                        onValidityChange={(isValid) =>
                                                            handleValidityChange(key, isValid)
                                                        }
                                                    />
                                                );
                                            case 'severity':
                                                return (
                                                    <SeverityInput
                                                        displayName={field.displayName}
                                                        value={numericValue}
                                                        onChange={(val) => setField(key, 'severity', val)}
                                                        onValidityChange={(isValid) =>
                                                            handleValidityChange(key, isValid)
                                                        }
                                                    />
                                                );
                                            case 'string-array': {
                                                const arrayVal = Array.isArray(configVal) ? configVal as string[] : [];
                                                return (
                                                    <StringArrayInput
                                                        items={arrayVal}
                                                        displayName={field.displayName}
                                                        description={field.description}
                                                        itemLabel={field.jsonSchema.itemLabel}
                                                        itemPlaceholder={field.jsonSchema.itemPlaceholder}
                                                        onChange={(val) => setStringArrayField(key, val)}
                                                    />
                                                );
                                            }
                                            case 'string':
                                            default:
                                                return (
                                                    <StringInput
                                                        value={value}
                                                        displayName={field.displayName}
                                                        schema={{
                                                            minLength: field.jsonSchema.minLength ?? 4,
                                                            maxLength: field.jsonSchema.maxLength ?? 10
                                                        }}
                                                        onChange={(val) => setField(key, 'string', val)}
                                                        onValidityChange={(isValid) =>
                                                            handleValidityChange(key, isValid)
                                                        }
                                                    />
                                                );
                                        }
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomizableFields;
